import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Loader2, Target } from "lucide-react";
import { getJourney, compare } from "../lib/api";

const COLORS = ["#00F0FF", "#B026FF", "#FFD700"];
const AXES = [
  { key: "income_potential", label: "Income" },
  { key: "net_worth_potential", label: "Net Worth" },
  { key: "family_fulfillment", label: "Family" },
  { key: "lifestyle_freedom", label: "Freedom" },
  { key: "free_time", label: "Free Time" },
  { key: "financial_stability", label: "Stability" },
  { key: "risk_level", label: "Risk" },
  { key: "growth_potential", label: "Growth" },
];

export default function Compare() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState([]);
  const [futures, setFutures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const j = await getJourney(id);
        const saved = j.saved_futures || [];
        const picks = saved.slice(-3);
        if (picks.length === 0) {
          setLoading(false);
          return;
        }
        setFutures(picks);
        const data = await compare(id, picks);
        setComparisons(data.comparisons || []);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  return (
    <div className="min-h-screen px-5 md:px-8 py-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(`/journey/${id}`)} className="btn-pill text-xs" data-testid="back-journey-btn">
          <ChevronLeft size={14} /> Journey
        </button>
        <div className="font-mono-label text-white/50">Compare your futures</div>
        <div style={{ width: 60 }} />
      </div>

      <h1 className="text-3xl md:text-4xl font-display font-light mb-2 text-center">
        Three lives, <span className="text-glow-purple">side by side</span>
      </h1>
      <p className="text-sm text-white/50 text-center mb-10 max-w-xl mx-auto">
        A snapshot of what each pathway holds — opportunities, trade-offs, and the shape of a day.
      </p>

      {loading && (
        <div className="text-center py-20 text-white/50">
          <Loader2 className="animate-spin mx-auto mb-3" /> Weighing your futures…
        </div>
      )}

      {!loading && futures.length === 0 && (
        <div className="glass rounded-3xl p-10 text-center max-w-xl mx-auto">
          <p className="text-white/70 mb-4">You haven't saved any futures yet.</p>
          <p className="text-sm text-white/50 mb-6">
            Head back into your journey and tap "Save to compare" on any pathway card.
          </p>
          <button onClick={() => navigate(`/journey/${id}`)} className="btn-pill btn-pill-primary">
            Back to journey
          </button>
        </div>
      )}

      {!loading && futures.length > 0 && (
        <>
          <div className="grid gap-5 md:grid-cols-3 mb-10">
            {futures.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass rounded-2xl p-6"
                style={{ borderColor: COLORS[i] + "66", boxShadow: `0 0 24px ${COLORS[i]}33` }}
                data-testid={`compare-card-${i}`}
              >
                <div className="font-mono-label mb-2" style={{ color: COLORS[i] }}>Future 0{i + 1}</div>
                <h3 className="text-xl font-display mb-3" style={{ textShadow: `0 0 14px ${COLORS[i]}66` }}>{f.title}</h3>
                <p className="text-sm text-white/60 mb-4">{f.summary}</p>
                <div className="space-y-1 text-xs text-white/55">
                  <div><span className="font-mono-label mr-2">Income</span>{f.income_estimate}</div>
                  <div><span className="font-mono-label mr-2">Net Worth</span>{f.net_worth}</div>
                  <div><span className="font-mono-label mr-2">Lifestyle</span>{f.lifestyle}</div>
                  <div><span className="font-mono-label mr-2">Risks</span>{f.risks}</div>
                </div>
                <button
                  onClick={() => navigate(`/plan/${id}?title=${encodeURIComponent(f.title)}&summary=${encodeURIComponent(f.summary)}`)}
                  className="mt-5 btn-pill btn-pill-gold text-xs w-full justify-center"
                  data-testid={`compare-plan-${i}`}
                >
                  <Target size={12} /> Turn this into a plan
                </button>
              </motion.div>
            ))}
          </div>

          {comparisons.length > 0 && (
            <div className="glass rounded-3xl p-6 md:p-8" data-testid="comparison-grid">
              <div className="font-mono-label text-white/50 mb-5">Side-by-side scoring (0–10)</div>
              <div className="space-y-5">
                {AXES.map((ax) => (
                  <div key={ax.key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-display text-sm text-white/80">{ax.label}</div>
                    </div>
                    <div className="grid gap-2">
                      {comparisons.map((c, i) => {
                        const v = c.scores?.[ax.key] ?? 5;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-24 shrink-0 text-xs text-white/50 truncate">{c.title}</div>
                            <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${(v / 10) * 100}%` }}
                                transition={{ duration: 0.8, delay: i * 0.1 }}
                                style={{
                                  height: "100%",
                                  background: COLORS[i],
                                  boxShadow: `0 0 10px ${COLORS[i]}`,
                                }}
                              />
                            </div>
                            <div className="w-8 text-right text-xs text-white/60">{v}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                {comparisons.map((c, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <span className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: COLORS[i], boxShadow: `0 0 10px ${COLORS[i]}` }} />
                    <div>
                      <div className="font-display text-sm" style={{ color: COLORS[i] }}>{c.title}</div>
                      <div className="text-sm text-white/70">{c.verdict}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
